import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TenantAgentConfigSetting,
  UpdateInternalTenantRequest,
  UpdateInternalTenantAgentModelsRequest,
} from '@resolve-ai/shared';
import { tenantsApi } from '../lib/tenants-api';
import { ApiError } from '../lib/api-client';

export type TenantDetailTab = 'overview' | 'users' | 'projects' | 'models' | 'settings';

interface ToastState {
  isOpen: boolean;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

const EMPTY_TOAST: ToastState = { isOpen: false, message: '', variant: 'info' };

// Stable fallback so consumers don't see a new array on every render while loading
const NO_AGENT_MODELS: TenantAgentConfigSetting[] = [];

function describeUpdate(payload: UpdateInternalTenantRequest): string {
  if (payload.codeReviewGraphEnabled === undefined) return 'Tenant updated successfully';

  const state = payload.codeReviewGraphEnabled ? 'on' : 'off';
  return `Code review graph turned ${state}. Changes apply to new tasks.`;
}

export function useTenantDetailPage(tenantId: string, activeTab: TenantDetailTab) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<ToastState>(EMPTY_TOAST);

  const overviewQuery = useQuery({
    queryKey: ['tenant', tenantId, 'overview'],
    queryFn: () => tenantsApi.getOverview(tenantId),
  });

  const usersQuery = useQuery({
    queryKey: ['tenant', tenantId, 'users'],
    queryFn: () => tenantsApi.getUsers(tenantId),
    enabled: activeTab === 'users',
  });

  const projectsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'projects'],
    queryFn: () => tenantsApi.getProjects(tenantId),
    enabled: activeTab === 'projects',
  });

  const agentModelsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'agent-models'],
    queryFn: () => tenantsApi.getAgentModels(tenantId),
    enabled: activeTab === 'models',
  });

  const showToast = (message: string, variant: ToastState['variant']) =>
    setToast({ isOpen: true, message, variant });

  // Returned so a mutation can stay pending until the refetch lands
  const invalidateTenant = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] }),
      queryClient.invalidateQueries({ queryKey: ['tenants'] }),
    ]);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateInternalTenantRequest) => tenantsApi.update(tenantId, payload),
    // Awaiting the refetch keeps isPending true until the page shows the
    // saved value, so a toggle can't flick back or be clicked twice.
    onSuccess: async (_data, payload) => {
      await invalidateTenant();
      showToast(describeUpdate(payload), 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update tenant', 'error');
    },
  });

  const agentModelsMutation = useMutation({
    mutationFn: (models: UpdateInternalTenantAgentModelsRequest['models']) =>
      tenantsApi.updateAgentModels(tenantId, { models }),
    onSuccess: (data) => {
      queryClient.setQueryData(['tenant', tenantId, 'agent-models'], data);
      showToast('AI models updated. Changes apply to new tasks.', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update AI models', 'error');
    },
  });

  return {
    overview: overviewQuery.data?.overview,
    isOverviewLoading: overviewQuery.isLoading,
    hasOverviewError: overviewQuery.isError,
    users: usersQuery.data?.users ?? [],
    isUsersLoading: usersQuery.isLoading,
    projects: projectsQuery.data?.projects ?? [],
    isProjectsLoading: projectsQuery.isLoading,
    agentModels: agentModelsQuery.data?.agentModels ?? NO_AGENT_MODELS,
    isAgentModelsLoading: agentModelsQuery.isLoading,
    hasAgentModelsError: agentModelsQuery.isError,
    updateMutation,
    agentModelsMutation,
    toast: { ...toast, close: () => setToast(EMPTY_TOAST) },
  };
}
